import { useEffect, useMemo, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import {
  doGetAuditsData,
  doGetData,
  doUpdateData,
  emptyOrganization,
  luHandleAppiError,
  normalizeU3148OrganizationFromApi,
  type U3148Organization,
} from "../U3148Service";
import { useU3148Context } from "../U3148Context";
import { NS, U3148ROLES } from "../U3148";
import { U3148OrganizationFormToolBar } from "./form/u3148Organization/U3148OrganizationFormToolBar";
import U3148OrganizationBody from "./form/u3148Organization/U3148OrganizationBody";
import U3148OrganizationRoleBody from "./form/u3148Organization/u3148OrganizationRoleTab/U3148OrganizationRoleBody";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U3148OrganizationUserBody from "./form/u3148Organization/u3148OrganizationAffiliationTab/U3148OrganizationAffiliationBody";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U3148OrganizationPersonalizationBody from "./form/u3148Organization/u3148OrganizationPersonalizationTab/U3148OrganizationPersonalizationBody";
import U3148OrganizationGoogleCalendarBody from "./form/u3148Organization/u3148OrganizationGoogleCalendarTab/U3148OrganizationGoogleCalendarBody";
import { flushPendingOrganizationIconChanges } from "./form/u3148Organization/U3148UseOrganizationIconHandlers";
import {
  clearOrganizationIconPendingAttributes,
  getOrganizationIconPending,
  hasOrganizationIconPendingChanges,
} from "./form/u3148Organization/u3148OrganizationIconPending";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U3148OrganizationFormExtendedHandlers extends U2000FormStateHandlersReturn<U3148Organization> {
  modifyOrganization: () => void;
}

const U3148OrganizationForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // Form state and reducer for u3148Organization
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t, i18n } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers, orgAffiliationCurrentTab, setOrgAffiliationCurrentTab, currentOrganizationId } =
    useU3148Context();
  const [googleCalendarStatusRefreshKey, setGoogleCalendarStatusRefreshKey] = useState(0);

  // Form Handlers and default methods
  const validateForm = (organization: U3148Organization) => {
    const result: Record<string, string> = {};
    if (!organization.organizationName || organization.organizationName.trim() === "") {
      result["username"] = t("errUserNameRequired", { ns: NS });
    }
    return result;
  };

  // Business Logic Handlers for U3148Organization
  const u3148OrganizationBL: U2000FormSBusinessHandlers<U3148Organization> = {
    newRecord: () => {
      return emptyOrganization;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (organization: U3148Organization, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(organization);
      if (Object.keys(errors).length === 0) {
        await handleSave.saveData(organization, editMode);
      } else {
        u3148OrganizationFormStateExtendeHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire — reste sur le formulaire courant
    cancelForm: () => {
      const organization = u3148OrganizationFormStateExtendeHandlers.getOldData() as U3148Organization | null;
      if (organization) {
        if ("roles" in organization && Array.isArray((organization as any).roles)) {
          (organization as any).roles = (organization as any).roles.map((role: any) => ({ ...role, idChecked: false }));
        }
        u3148OrganizationFormStateExtendeHandlers.setData({ ...organization });
      }
      u3148OrganizationFormStateExtendeHandlers.setCustomAttributes(
        clearOrganizationIconPendingAttributes(u3148OrganizationFormStateExtendeHandlers.getCustomAttributes())
      );

      if (id) {
        handleGet.getData(id);
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (organization) => {
      if (organization.id) {
        handleGet.getData(organization.id);
      }
    },

    // pas de pagination liste en U3148
    onChangePageForm: () => {},

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U3148");
    },
  };

  // Handlers pour le formulaire d'une organisation
  const u3148OrganizationFormStateHandlers = useU2000FormStateHandlers<U3148Organization>({
    formState,
    formStateDispatch,
    businessHandlers: u3148OrganizationBL,
  });

  // extended methods
  const u3148OrganizationFormStateExtendeHandlers: U3148OrganizationFormExtendedHandlers = {
    ...u3148OrganizationFormStateHandlers,

    modifyOrganization: () => {
      u3148OrganizationFormStateExtendeHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
  };

  // Call Get Handlers for Logical Unit
  const handleGet = useU2000GetHandlers<U3148Organization & { id: string }>({
    doGetFn: (orgId: string) => {
      return doGetData(u2000AxiosAuthenticated, orgId);
    },
    onSuccess: (logicalUnit: U3148Organization & { id: string }) => {
      const normalized = normalizeU3148OrganizationFromApi(logicalUnit, i18n.language);
      gridStateHandlers.setDataById(normalized.id, normalized);
      u3148OrganizationFormStateExtendeHandlers.setData(normalized);
      u3148OrganizationFormStateExtendeHandlers.setCustomAttributes(
        clearOrganizationIconPendingAttributes(u3148OrganizationFormStateExtendeHandlers.getCustomAttributes())
      );

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(logicalUnit.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }, { label: logicalUnit.organizationName }]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3148OrganizationFormStateExtendeHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit — EDIT only
  const handleSave = useU2000SaveHandlers<U3148Organization>({
    doSaveFn: (organization: U3148Organization, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, organization.id, organization);
      }
      return Promise.resolve(undefined);
    },
    onSuccess: async (organization: U3148Organization & { id: string }, _editMode: U2000FormStateEnumEditMode) => {
      let normalized = normalizeU3148OrganizationFromApi(organization, i18n.language);
      const pendingIcon = getOrganizationIconPending(u3148OrganizationFormStateExtendeHandlers.getCustomAttributes());

      if (hasOrganizationIconPendingChanges(pendingIcon) && normalized.id) {
        try {
          const iconUpdated = await flushPendingOrganizationIconChanges(u2000AxiosAuthenticated, normalized.id, pendingIcon);
          if (iconUpdated) {
            normalized = normalizeU3148OrganizationFromApi({ ...normalized, ...iconUpdated }, i18n.language);
          }
        } catch (iconError: unknown) {
          const errorResult = U2000HandleApiError(iconError as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
          if (errorResult.genericErrors?.length) {
            errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
          } else {
            toast.error(t("msgOrganizationIconSaveFailed", { ns: NS }));
          }
        }
      }

      u3148OrganizationFormStateExtendeHandlers.setCustomAttributes(
        clearOrganizationIconPendingAttributes(u3148OrganizationFormStateExtendeHandlers.getCustomAttributes())
      );

      gridStateHandlers.setDataById(normalized.id, normalized);
      u3148OrganizationFormStateExtendeHandlers.afterSaveData(normalized);
      toast.success(t("msgOrganizationSaved", { ns: NS }));

      if (orgAffiliationCurrentTab === "tabGoogleCalendar") {
        setGoogleCalendarStatusRefreshKey((k) => k + 1);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3148OrganizationFormStateExtendeHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3148OrganizationFormStateExtendeHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Initialisation au montage et quand id change
  useEffect(() => {
    if (id === undefined) return;
    if (!currentOrganizationId) return;
    if (id !== currentOrganizationId) return;
    handleGet.getData(id);
  }, [id, currentOrganizationId]);

  // Retour OAuth Google Calendar (?googleCalendar=connected)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("googleCalendar") !== "connected") return;
    toast.success(t("msgGoogleCalendarConnected", { ns: NS }));
    setOrgAffiliationCurrentTab("tabGoogleCalendar");
    setGoogleCalendarStatusRefreshKey((k) => k + 1);
    window.history.replaceState({}, "", location.pathname + location.hash);
  }, [location.search, location.pathname, location.hash, t, toast, setOrgAffiliationCurrentTab]);

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabUser",
      label: t("tabUsers", { ns: NS }),
      content: <U3148OrganizationUserBody u3148OrganizationFormHandlers={u3148OrganizationFormStateExtendeHandlers} />,
    },
    {
      id: "tabRole",
      label: t("tabRoles", { ns: NS }),
      content: <U3148OrganizationRoleBody u3148OrganizationFormHandlers={u3148OrganizationFormStateExtendeHandlers} />,
    },
    {
      id: "tabPersonalization",
      label: t("tabPersonalization", { ns: NS }),
      content: <U3148OrganizationPersonalizationBody u3148OrganizationFormHandlers={u3148OrganizationFormStateExtendeHandlers} />,
    },
    {
      id: "tabGoogleCalendar",
      label: t("tabGoogleCalendar", { ns: NS }),
      content: (
        <U3148OrganizationGoogleCalendarBody
          u3148OrganizationFormHandlers={u3148OrganizationFormStateExtendeHandlers}
          statusRefreshKey={googleCalendarStatusRefreshKey}
        />
      ),
    },
    hasRole(U3148ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u3148OrganizationFormStateExtendeHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U3148OrganizationAudit" />,
    },
  ].filter(Boolean);

  // Détermination dynamique du defaultTab selon la priorité : Users > Roles > Personalization > Audits
  const tabPriority = ["tabUser", "tabRole", "tabPersonalization", "tabGoogleCalendar", "tabAudits"];
  const availableTabIds = formTabsComponent.map((tab) => tab.id);
  const defaultTab = orgAffiliationCurrentTab ? orgAffiliationCurrentTab : tabPriority.find((tabId) => availableTabIds.includes(tabId)) || availableTabIds[0];

  // import pour eciter les boocles infinies
  const formEditMode = useMemo(() => u3148OrganizationFormStateExtendeHandlers.getFormEditMode(), [u3148OrganizationFormStateExtendeHandlers]);

  // permet de mettre à jour les donnees apres le changement du mode edit (ADD ou EDIT)
  useEffect(() => {
    if (formEditMode === U2000FormStateEnumEditMode.EDIT) {
    }
  }, [formEditMode]);

  // Redirection si l'id URL n'est pas l'organisation courante
  if (id && currentOrganizationId && id !== currentOrganizationId) {
    return <Navigate to={`/U3148/${currentOrganizationId}`} replace />;
  }

  // s'assurer que les données sont chargées avant le rendu
  if (u3148OrganizationFormStateExtendeHandlers.getData() === null) {
    return null;
  }

  // Render
  return (
    <U2000DefaultForm
      actionsBtns={<U3148OrganizationFormToolBar formStateHandlers={u3148OrganizationFormStateExtendeHandlers as any} />}
      searchStateHandlers={searchStateHandlers}
      gridStateHandlers={gridStateHandlers}
      formStateHandlers={u3148OrganizationFormStateExtendeHandlers as any}
      showBackToList={false}
    >
      <U3148OrganizationBody formStateHandlers={u3148OrganizationFormStateExtendeHandlers as any} t={t} />

      <U2000Tab
        className="mt-4"
        tabs={formTabsComponent}
        defaultTab={defaultTab}
        activeTab={orgAffiliationCurrentTab ?? defaultTab}
        setActiveTab={(tab) => setOrgAffiliationCurrentTab(typeof tab === "string" ? tab : String(tab))}
      />
    </U2000DefaultForm>
  );
};

export default U3148OrganizationForm;
